import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Platform, type View } from "react-native";
import { useLatestRef } from "../../hooks/use-latest-ref";
import type { MediaCarouselItem } from "./MediaCarousel.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseMediaCarouselOptions<T extends MediaCarouselItem = MediaCarouselItem> {
  readonly items: readonly T[];
  /** Active index (controlled). Omit to let the hook own the index. */
  readonly index?: number | undefined;
  /** Initial active index when uncontrolled. Defaults to 0. */
  readonly defaultIndex?: number | undefined;
  readonly onIndexChange?: ((index: number, item: T) => void) | undefined;
  /** When true, navigating past either end wraps around. Defaults to false. */
  readonly loop?: boolean | undefined;
  /** Neighbours on each side of the active item to keep mounted. Defaults to 1. */
  readonly preloadCount?: number | undefined;
  /**
   * Whether Left/Right arrow keys navigate while focus is inside `containerRef`.
   * Defaults to true. Web only — see `containerRef`.
   */
  readonly enableKeyboardNavigation?: boolean | undefined;
}

/** One media slot the carousel keeps mounted. */
export interface MountedMediaItem<T extends MediaCarouselItem = MediaCarouselItem> {
  readonly item: T;
  readonly index: number;
  readonly isActive: boolean;
}

export interface UseMediaCarouselReturn<T extends MediaCarouselItem = MediaCarouselItem> {
  readonly index: number;
  readonly activeItem: T | undefined;
  readonly itemCount: number;
  readonly canGoPrevious: boolean;
  readonly canGoNext: boolean;
  readonly goToPrevious: () => void;
  readonly goToNext: () => void;
  readonly goToIndex: (index: number) => void;
  /**
   * The active item plus its preloaded neighbours, in index order. Render only
   * these — everything else stays unmounted so its media is never fetched.
   */
  readonly mountedItems: readonly MountedMediaItem<T>[];
  /**
   * Attach to the view that should own Left/Right arrow keys. The keys are
   * handled only while focus is inside it, so a page can hold several carousels
   * without them fighting, and so arrow keys still reach a text field a product
   * puts in the detail area. Make the view a tab stop (`tabIndex={0}`) for
   * keyboard users to reach it.
   *
   * Web only: a native `View` receives no key events.
   */
  readonly containerRef: RefObject<View | null>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Navigation state for a media carousel: which item is active, which
 * neighbours are worth keeping mounted, and how to move between them.
 *
 * `MediaCarousel` uses this internally, so reach for it directly only when the
 * navigation has to live outside the carousel — driving the index from a route
 * param, wiring a keyboard shortcut, or laying the controls out somewhere the
 * overlay slots can't reach.
 */
export function useMediaCarousel<T extends MediaCarouselItem = MediaCarouselItem>(
  options: UseMediaCarouselOptions<T>,
): UseMediaCarouselReturn<T> {
  const {
    items,
    index: controlledIndex,
    defaultIndex = 0,
    onIndexChange,
    loop = false,
    preloadCount = 1,
    enableKeyboardNavigation = true,
  } = options;

  const itemCount = items.length;
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultIndex);

  // Clamped on read rather than synced in an Effect: `items` can shrink under a
  // stale index (a page of detections is refetched, an item is deleted), and
  // clamping here keeps the rendered index valid without an extra render pass.
  const index = clampIndex(controlledIndex ?? uncontrolledIndex, itemCount);

  const isControlled = controlledIndex !== undefined;
  const itemsRef = useLatestRef(items);
  const indexRef = useLatestRef(index);
  const onIndexChangeRef = useLatestRef(onIndexChange);

  const goToIndex = useCallback(
    (requested: number) => {
      const count = itemsRef.current.length;
      if (count === 0) return;

      const target = clampIndex(requested, count);
      if (target === indexRef.current) return;

      if (!isControlled) setUncontrolledIndex(target);

      const item = itemsRef.current[target];
      if (item !== undefined) onIndexChangeRef.current?.(target, item);
    },
    [isControlled, itemsRef, indexRef, onIndexChangeRef],
  );

  const step = useCallback(
    (delta: number) => {
      const count = itemsRef.current.length;
      if (count === 0) return;

      const target = indexRef.current + delta;
      if (target >= 0 && target < count) {
        goToIndex(target);
        return;
      }
      if (loop) goToIndex(wrapIndex(target, count));
    },
    [goToIndex, itemsRef, indexRef, loop],
  );

  const goToPrevious = useCallback(() => {
    step(-1);
  }, [step]);

  const goToNext = useCallback(() => {
    step(1);
  }, [step]);

  const mountedItems = useMemo(
    () => collectMountedItems(items, index, preloadCount, loop),
    [items, index, preloadCount, loop],
  );

  const containerRef = useRef<View>(null);
  const keyboardNavigable = enableKeyboardNavigation && itemCount > 1;
  useArrowKeyNavigation(containerRef, goToPrevious, goToNext, keyboardNavigable);
  useFocusRetention(containerRef, keyboardNavigable);

  return {
    index,
    activeItem: items[index],
    itemCount,
    canGoPrevious: itemCount > 1 && (loop || index > 0),
    canGoNext: itemCount > 1 && (loop || index < itemCount - 1),
    goToPrevious,
    goToNext,
    goToIndex,
    mountedItems,
    containerRef,
  };
}

// ---------------------------------------------------------------------------
// Arrow key navigation
// ---------------------------------------------------------------------------

/**
 * Elements that own the arrow keys themselves. Every slot holds arbitrary
 * product content, and a control that moves a caret, a value or a playhead has
 * a stronger claim on Left/Right than paging does.
 *
 * A clip player is the case that matters most: a focused `video` seeks on
 * Left/Right, and a custom scrubber is conventionally a `slider`. Note that
 * `tab` is deliberately absent — the thumbnails are tabs, and arrowing between
 * them is exactly the paging this hook provides.
 *
 * Anything outside this list can opt out at the source by calling
 * `stopPropagation()` on its own keydown, since this listener sits on
 * `document`, or by calling `preventDefault()`.
 */
const KEY_OWNING_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[contenteditable='']",
  "video",
  "audio",
  "[role='slider']",
  "[role='spinbutton']",
].join(", ");

function useArrowKeyNavigation(
  containerRef: RefObject<View | null>,
  goToPrevious: () => void,
  goToNext: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    // Native gets nothing: a `View` receives no key events, and hardware
    // keyboard support there would need a different mechanism entirely.
    if (Platform.OS !== "web" || !enabled) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      // Something nearer the key already acted on it — a media player seeking,
      // for instance. Paging on top of that would fight it.
      if (event.defaultPrevented) return;

      // The listener is on `document` rather than the container so that it
      // survives the container element being swapped out (empty state to
      // populated, for instance) without needing to re-subscribe.
      const container = containerRef.current as unknown as HTMLElement | null;
      const target = event.target;
      if (container == null || !(target instanceof Node) || !container.contains(target)) return;
      if (target instanceof Element && target.closest(KEY_OWNING_SELECTOR) != null) return;

      event.preventDefault();
      if (event.key === "ArrowLeft") {
        goToPrevious();
      } else {
        goToNext();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, goToPrevious, goToNext, enabled]);
}

/**
 * Keeps focus inside the carousel when a control disables itself under the
 * user's finger.
 *
 * Clicking the next chevron onto the last item disables that chevron, and a
 * browser will not let a disabled control hold focus — so focus lands on the
 * document body and the arrow keys, which only apply while focus is inside,
 * quietly stop working. The signature is unmistakable: focus went nowhere
 * (`relatedTarget` is null) and the element it left is now disabled, which
 * cannot happen when the user simply clicks or tabs elsewhere.
 */
function useFocusRetention(containerRef: RefObject<View | null>, enabled: boolean): void {
  useEffect(() => {
    if (Platform.OS !== "web" || !enabled) return;

    function handleFocusOut(event: FocusEvent) {
      if (event.relatedTarget !== null) return;

      const container = containerRef.current as unknown as HTMLElement | null;
      const target = event.target;
      if (container == null || !(target instanceof Node) || !container.contains(target)) return;
      if (!(target instanceof HTMLButtonElement) || !target.disabled) return;

      container.focus();
    }

    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [containerRef, enabled]);
}

// ---------------------------------------------------------------------------
// Index maths
// ---------------------------------------------------------------------------

function clampIndex(value: number, count: number): number {
  if (count === 0) return 0;
  return Math.min(Math.max(Math.trunc(value), 0), count - 1);
}

function wrapIndex(value: number, count: number): number {
  return ((value % count) + count) % count;
}

function collectMountedItems<T extends MediaCarouselItem>(
  items: readonly T[],
  index: number,
  preloadCount: number,
  loop: boolean,
): readonly MountedMediaItem<T>[] {
  const count = items.length;
  if (count === 0) return [];

  const radius = Math.max(0, Math.trunc(preloadCount));
  const mountedIndexes = new Set<number>();

  for (let offset = -radius; offset <= radius; offset += 1) {
    const candidate = index + offset;
    if (candidate >= 0 && candidate < count) {
      mountedIndexes.add(candidate);
    } else if (loop) {
      mountedIndexes.add(wrapIndex(candidate, count));
    }
  }

  // Index order keeps the mounted slots in a stable DOM order as the active
  // item moves, so React reorders nothing and mounted media is never remounted.
  return [...mountedIndexes]
    .sort((a, b) => a - b)
    .flatMap((mountedIndex) => {
      const item = items[mountedIndex];
      return item === undefined ? [] : [{ item, index: mountedIndex, isActive: mountedIndex === index }];
    });
}
